import { useState } from 'react';
import { Link, useLocation, useMatch, useNavigate } from 'react-router-dom';
import { Button, cn } from '@immo/ui-kit';
import { type MeResponse, type Permission, type WorkspaceOrganizationListItem } from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';
import { isWorkspaceEntryBlocked } from '@/lib/auth/onboarding';
import { env } from '@/lib/env';
import { useWorkspaceRoutes } from '@/lib/workspace-path';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useNavCounts } from '@/lib/nav-counts';
import { mayCall } from '@/lib/permissions/route-gate';
import { ChevronDownIcon, ChevronLeftIcon, SettingsIcon, WarningTriangleIcon } from './icons';
import { navSections, settingsNavSections, type NavRow, type NavVisibilityContext } from './nav-items';
import { readNavOrigin, type NavOrigin } from './nav-origin';
import { offeredApplications, useLinkedApplications } from './use-linked-applications';
import { UserMenu } from './user-menu';
import { LocationSelector } from './location-selector';

const COLLAPSE_QUERY = '(max-width: 900px)';

type Props = {
  readonly me: MeResponse;
  readonly permissions: ReadonlyArray<Permission>;
  readonly narrowedOrganizationId: string | null;
  readonly seesAllStandorte: boolean;
  readonly organizations: ReadonlyArray<WorkspaceOrganizationListItem>;
  readonly workspaceId: string;
};

export function Sidebar({
  me,
  permissions,
  narrowedOrganizationId,
  seesAllStandorte,
  organizations,
  workspaceId,
}: Props) {
  const tShell = useTranslations('pages.shell.nav');
  const tSettings = useTranslations('pages.settings');
  const collapsed = useMediaQuery(COLLAPSE_QUERY);
  const navigate = useNavigate();
  const location = useLocation();
  const routes = useWorkspaceRoutes();
  const release = env.sentry.release;

  // Inside Settings the sidebar becomes the settings menu: a back affordance and
  // the section title replace the brand at the top, the settings sub-nav replaces
  // the main nav, and the brand drops to the footer.
  // `/members` is surfaced inside the settings sub-nav (it has no
  // `/settings` route of its own), so keep the settings menu mounted there too.
  // Paths are workspace-scoped (`/w/:id/settings…`), so match the sub-path rather
  // than the absolute prefix. `/members` and `/locations` are
  // surfaced inside the settings sub-nav, so keep the settings menu mounted there.
  const inSettings =
    location.pathname.includes(routes.settings()) ||
    location.pathname.includes(routes.members()) ||
    location.pathname.includes(routes.locations());
  const translate = inSettings ? (key: string) => tSettings(`nav.${key}`) : tShell;

  // Permission-gate the nav (ADR 0029): a row carrying a `gate` only
  // renders for a caller who may call it; sections that end up empty are dropped.
  // The settings sub-nav is gated the same way.
  // Workspaces the member would dead-end in (not-onboarded + can't onboard) —
  // the location selector renders these disabled so they can't switch into them.
  const blockedWorkspaceIds = new Set(
    me.workspaces.filter((ws) => isWorkspaceEntryBlocked(me, ws.id)).map((ws) => ws.id),
  );

  // Two conditions of genuinely different kinds, deliberately kept apart
  // (IMM-751.4): a `gate` answers "may this member", a `visibleWhen`
  // predicate answers "does this workspace have it at all". Collapsing the
  // second into a permission would make RBAC a licensing model.
  //
  // Asked only inside settings, where the one row using it lives: the main
  // navigation has no `visibleWhen` row, so issuing the read on every workspace
  // page would be a request per navigation that nothing reads.
  const [applicationRetry, setApplicationRetry] = useState(0);
  const applications = useLinkedApplications(workspaceId, inSettings ? permissions : [], applicationRetry);
  const navContext: NavVisibilityContext = { linkedApplications: offeredApplications(applications) };
  const sections = (inSettings ? settingsNavSections : navSections)
    .map((section) => ({
      ...section,
      items: section.items.filter(
        (item) =>
          (item.gate === undefined || mayCall(permissions, item.gate)) &&
          (item.visibleWhen === undefined || item.visibleWhen(navContext)),
      ),
    }))
    .filter((section) => section.items.length > 0);

  // A detail page opened from a list keeps that list's row highlighted (IMM-1347). Only an origin a rendered row
  // carries counts, so a gated-off row never leaves the sidebar with nothing highlighted.
  const origin = readNavOrigin(location.state);
  const highlight =
    origin !== null && sections.some((section) => section.items.some((item) => item.origin === origin)) ? origin : null;

  return (
    <aside
      className={cn(
        'flex h-full min-h-0 shrink-0 flex-col border-r border-border bg-canvas',
        collapsed ? 'w-14' : 'w-sidebar',
      )}
    >
      {inSettings ? (
        <div
          className={cn(
            'flex h-topbar items-center border-b border-border',
            collapsed ? 'justify-center px-0' : 'gap-2 px-3',
          )}
        >
          <button
            type="button"
            onClick={() => navigate(routes.home(), { viewTransition: true })}
            aria-label={tSettings('back')}
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-text-secondary transition-colors duration-micro ease-out hover:bg-hover-bg hover:text-text-primary focus-visible:shadow-focus focus-visible:outline-none"
          >
            <ChevronLeftIcon className="h-4 w-4" aria-hidden />
          </button>
          {!collapsed && (
            <span className="text-base font-semibold tracking-tight text-text-primary">{tSettings('title')}</span>
          )}
        </div>
      ) : (
        <LocationSelector
          organizations={organizations}
          narrowedOrganizationId={narrowedOrganizationId}
          seesAllStandorte={seesAllStandorte}
          collapsed={collapsed}
          workspaces={me.workspaces}
          activeWorkspaceId={workspaceId}
          blockedWorkspaceIds={blockedWorkspaceIds}
        />
      )}

      <nav className="flex-1 overflow-y-auto px-2 py-3">
        {sections.map((section) => (
          <div key={section.labelKey} className="mb-5 last:mb-0">
            {!collapsed && (
              <div className="px-2 pb-1.5 text-xs font-semibold uppercase tracking-wide text-text-tertiary">
                {translate(section.labelKey)}
                {section.subtitleKey !== undefined && (
                  <span className="ml-1.5 font-normal normal-case tracking-normal text-text-tertiary/70">
                    · {translate(section.subtitleKey)}
                  </span>
                )}
              </div>
            )}
            <div className="space-y-0.5">
              {section.items.map((item) =>
                item.view !== undefined ? (
                  <NavRowViewLink
                    key={item.labelKey}
                    item={item}
                    translate={translate}
                    collapsed={collapsed}
                    highlight={highlight}
                  />
                ) : item.children !== undefined ? (
                  <NavRowGroup
                    key={item.labelKey}
                    item={item}
                    translate={translate}
                    collapsed={collapsed}
                    highlight={highlight}
                  />
                ) : (
                  <NavRowLink
                    key={item.labelKey}
                    item={item}
                    translate={translate}
                    collapsed={collapsed}
                    highlight={highlight}
                  />
                ),
              )}
            </div>
          </div>
        ))}
        {inSettings && applications.status === 'failed' && (
          <div role="alert" className={cn('space-y-2 rounded-md py-3 text-base-sm', !collapsed && 'px-2')}>
            <p className={collapsed ? 'sr-only' : 'text-danger-700'}>{tSettings('nav.load-error')}</p>
            <Button
              type="button"
              variant="secondary"
              size={collapsed ? 'icon' : 'sm'}
              aria-label={collapsed ? `${tSettings('nav.load-error')} ${tSettings('nav.retry')}` : undefined}
              title={collapsed ? `${tSettings('nav.load-error')} ${tSettings('nav.retry')}` : undefined}
              onClick={() => setApplicationRetry((attempt) => attempt + 1)}
            >
              {collapsed ? (
                <WarningTriangleIcon className="h-4 w-4 text-danger-700" aria-hidden />
              ) : (
                tSettings('nav.retry')
              )}
            </Button>
          </div>
        )}
      </nav>

      {!inSettings && !collapsed && (
        <div className="border-t border-border px-2 py-2">
          <Link to={routes.settings()} viewTransition className={baseNavClass}>
            <SettingsIcon className="h-4 w-4 shrink-0" aria-hidden />
            <span>{tSettings('title')}</span>
          </Link>
        </div>
      )}

      <div className="flex h-topbar items-center border-t border-border px-2">
        {inSettings ? (
          // In Settings the footer shows the selected location — no user menu.
          <LocationSelector
            organizations={organizations}
            narrowedOrganizationId={narrowedOrganizationId}
            seesAllStandorte={seesAllStandorte}
            collapsed={collapsed}
            className="border-0 px-2"
            workspaces={me.workspaces}
            activeWorkspaceId={workspaceId}
            blockedWorkspaceIds={blockedWorkspaceIds}
          />
        ) : (
          <UserMenu me={me} workspaceId={workspaceId} collapsed={collapsed} />
        )}
      </div>
      {inSettings && !collapsed && release !== undefined && (
        <p className="px-3.5 pb-2 text-xs text-text-tertiary">{tSettings('version', { version: release })}</p>
      )}
    </aside>
  );
}

type Translate = (key: string) => string;

type RowProps = {
  readonly item: NavRow;
  readonly translate: Translate;
  readonly collapsed: boolean;
  readonly highlight: NavOrigin | null;
};

// The origin wins over the path; without one, the path decides as it always has.
const rowActive = (item: NavRow, pathActive: boolean, highlight: NavOrigin | null): boolean =>
  highlight !== null ? item.origin === highlight : pathActive;

// Every expanded row paints the kit focus ring, like the collapsed rail and the
// E-Mails group link. Without it a plain row fell back to the browser's black
// `outline: auto` (IMM-1348).
const baseNavClass =
  'flex h-sidebar-item items-center gap-2.5 rounded-md px-2 text-base-sm text-text-secondary transition-colors duration-micro ease-out hover:bg-hover-bg hover:text-text-primary focus-visible:shadow-focus focus-visible:outline-none';
const collapsedNavClass =
  'flex h-sidebar-item items-center justify-center rounded-md text-text-secondary transition-colors duration-micro ease-out hover:bg-hover-bg hover:text-text-primary focus-visible:shadow-focus focus-visible:outline-none';
const activeNavClass = 'bg-selected-bg font-medium text-text-primary';

function RailTooltip({ label }: { label: string }) {
  return (
    <span
      role="tooltip"
      className="pointer-events-none absolute left-full top-1/2 z-50 ml-2 -translate-y-1/2 whitespace-nowrap rounded-md bg-tooltip px-2 py-1 text-sm text-neutral-0 opacity-0 shadow-md transition-opacity duration-micro ease-out group-hover:opacity-100 group-focus-within:opacity-100"
    >
      {label}
    </span>
  );
}

const disabledNavClass =
  'flex h-sidebar-item items-center gap-2.5 rounded-md px-2 text-base-sm text-text-tertiary opacity-60';

// A row with `children` renders as the row itself plus a disclosure control that
// reveals the sub-rows. Clicking the row opens the Posteingang AND expands the
// sub-rows — the destination and its siblings are the same thought, so one click
// serves both; the chevron is only there to collapse again. Open state is seeded
// from the route (a mail route starts expanded) and is then the member's to keep.
//
// The row's background lives on the WRAPPER, not on the link, so hover / active
// wash the whole row — chevron included — instead of stopping at the label. That
// also keeps the chevron a real sibling `<button>`: an interactive control nested
// inside an `<a>` would be invalid.
//
// In the collapsed icon rail there is no room to nest, so the parent renders as a
// plain icon link and the children are unreachable from the rail — same as the
// rail's other rows, which drop their labels there too.
function NavRowGroup({ item, translate, collapsed, highlight }: RowProps) {
  const routes = useWorkspaceRoutes();
  const to = item.to!(routes);
  const children = item.children ?? [];
  // The `useMatch` path rule shared by every row kind, read here so the wrapper can carry the state.
  const active = rowActive(item, useMatch({ path: to, end: item.end ?? false }) !== null, highlight);
  const [open, setOpen] = useState(active);
  const label = translate(item.labelKey);

  if (collapsed) return <NavRowLink item={item} translate={translate} collapsed highlight={highlight} />;

  return (
    <div>
      <div className={cn(baseNavClass, active && activeNavClass)}>
        <Link
          to={to}
          viewTransition
          aria-current={active ? 'page' : undefined}
          onClick={() => setOpen(true)}
          className="flex min-w-0 flex-1 items-center gap-2.5 rounded-md focus-visible:shadow-focus focus-visible:outline-none"
        >
          <item.icon className="h-4 w-4 shrink-0" aria-hidden />
          <span className="truncate">{label}</span>
        </Link>
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          aria-expanded={open}
          aria-label={translate(open ? `${item.labelKey}-collapse` : `${item.labelKey}-expand`)}
          // No background of its own: the wrapper owns the row's fill, so a second
          // hover surface here would show as a patch inside it.
          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-text-tertiary transition-colors duration-micro ease-out hover:text-text-primary focus-visible:shadow-focus focus-visible:outline-none"
        >
          <ChevronDownIcon
            className={cn('h-3.5 w-3.5 transition-transform duration-micro ease-out', !open && '-rotate-90')}
            aria-hidden
          />
        </button>
      </div>
      {open && (
        <div className="mt-0.5 space-y-0.5 pl-4">
          {children.map((child) => (
            <NavRowLink
              key={child.labelKey}
              item={child}
              translate={translate}
              collapsed={false}
              highlight={highlight}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * The row's counter, or `null` when there is nothing to say. `0` is deliberately
 * nothing: a member with no Bewertungen gets the empty state's sentence, not a
 * zero badge repeating it in the nav.
 */
function NavRowCount({ count }: { count: number | undefined }) {
  if (count === undefined || count === 0) return null;

  return (
    <span className="ml-auto inline-flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded-pill bg-subtle px-1.5 text-sm font-medium tabular-nums text-text-tertiary">
      {count}
    </span>
  );
}

function NavRowLink({ item, translate, collapsed, highlight }: RowProps) {
  const label = translate(item.labelKey);

  if (item.disabled === true) {
    if (collapsed) {
      return (
        <div className="group relative">
          <span
            aria-label={label}
            aria-disabled
            className={cn(collapsedNavClass, 'cursor-default opacity-60 hover:bg-transparent hover:text-text-tertiary')}
          >
            <item.icon className="h-4 w-4" aria-hidden />
          </span>
          <RailTooltip label={label} />
        </div>
      );
    }

    return (
      <span aria-disabled className={disabledNavClass}>
        <item.icon className="h-4 w-4 shrink-0" aria-hidden />
        <span>{label}</span>
      </span>
    );
  }

  return <NavRowActiveLink item={item} translate={translate} collapsed={collapsed} highlight={highlight} />;
}

// Split from `NavRowLink` so the route hooks sit ahead of no early return: a disabled row has no `to`.
function NavRowActiveLink({ item, translate, collapsed, highlight }: RowProps) {
  const label = translate(item.labelKey);
  const routes = useWorkspaceRoutes();
  const counts = useNavCounts();
  const count = item.countKey === undefined ? undefined : counts[item.countKey];
  const to = item.to!(routes);
  const pathActive = useMatch({ path: pathnameOf(to), end: item.end ?? false }) !== null;
  const active = rowActive(item, pathActive, highlight);

  if (collapsed) {
    return (
      <div className="group relative">
        <Link
          to={to}
          viewTransition
          aria-label={label}
          aria-current={active ? 'page' : undefined}
          className={cn(collapsedNavClass, active && activeNavClass)}
        >
          <item.icon className="h-4 w-4" aria-hidden />
        </Link>
        <RailTooltip label={label} />
      </div>
    );
  }

  return (
    <Link
      to={to}
      viewTransition
      aria-current={active ? 'page' : undefined}
      className={cn(baseNavClass, active && activeNavClass)}
    >
      <item.icon className="h-4 w-4 shrink-0" aria-hidden />
      <span>{label}</span>
      <NavRowCount count={count} />
      {item.badgeKey !== undefined && (
        <span aria-hidden className="ml-auto text-xs font-normal text-text-tertiary">
          {translate(item.badgeKey)}
        </span>
      )}
    </Link>
  );
}

const pathnameOf = (to: string) => to.split('?')[0]!;

// Two rows open the same path and differ by one query parameter, so the path-only
// `useMatch` rule would light both: the active state reads the parameter too.
function NavRowViewLink({ item, translate, collapsed, highlight }: RowProps) {
  const label = translate(item.labelKey);
  const routes = useWorkspaceRoutes();
  const counts = useNavCounts();
  const count = item.countKey === undefined ? undefined : counts[item.countKey];
  const to = item.to!(routes);
  const { param, value, fallback = false } = item.view!;
  const current = new URLSearchParams(useLocation().search).get(param);
  const onPath = useMatch({ path: pathnameOf(to), end: item.end ?? false }) !== null;
  const active = rowActive(item, onPath && (current === value || (current === null && fallback)), highlight);

  if (collapsed) {
    return (
      <div className="group relative">
        <Link
          to={to}
          viewTransition
          aria-label={label}
          aria-current={active ? 'page' : undefined}
          className={cn(collapsedNavClass, active && activeNavClass)}
        >
          <item.icon className="h-4 w-4" aria-hidden />
        </Link>
        <RailTooltip label={label} />
      </div>
    );
  }

  return (
    <Link
      to={to}
      viewTransition
      aria-current={active ? 'page' : undefined}
      className={cn(baseNavClass, active && activeNavClass)}
    >
      <item.icon className="h-4 w-4 shrink-0" aria-hidden />
      <span>{label}</span>
      <NavRowCount count={count} />
      {item.badgeKey !== undefined && (
        <span aria-hidden className="ml-auto text-xs font-normal text-text-tertiary">
          {translate(item.badgeKey)}
        </span>
      )}
    </Link>
  );
}
